import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import client from '@/lib/api';
import { Logo } from '@/components/Logo';
import { Card } from '@/components/ui/card';
import { StatusBadge } from '@/components/StatusBadge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { ShieldCheck, Lock, Users2, Truck, FileText } from 'lucide-react';

export default function GuestAuditView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    client.get(`/audit/guest/${token}`).then((r) => setData(r.data)).catch((e) => setError(e?.response?.data?.detail || 'This audit link is invalid or expired.'));
  }, [token]);

  return (
    <div className="min-h-screen bg-background">
      <div className="h-1 w-full bg-[hsl(var(--gold))]" />
      <header className="flex h-16 items-center justify-between border-b bg-white px-6">
        <Logo />
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--info))]/10 px-3 py-1 text-xs font-medium text-[hsl(var(--info))]"><Lock className="h-3.5 w-3.5" />Read-only Inspector View</span>
      </header>
      <main className="mx-auto w-full max-w-5xl px-4 py-8 md:px-8">
        {error ? (
          <Card className="p-12 text-center"><Lock className="mx-auto mb-3 h-8 w-8 text-muted-foreground" /><p className="text-sm text-muted-foreground">{error}</p></Card>
        ) : !data ? <Skeleton className="h-64" /> : (
          <>
            <div className="mb-5 flex items-center gap-3"><ShieldCheck className="h-8 w-8 text-[hsl(var(--primary))]" /><div><h1 className="font-display text-2xl font-semibold">{data.company}</h1><p className="text-sm text-muted-foreground">DOT / FMCSA compliance snapshot · Link valid until {new Date(data.expires_at).toLocaleDateString()}</p></div></div>
            <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[{ l: 'ISS Score', v: data.iss_score, i: ShieldCheck }, { l: 'Active Drivers', v: data.totals.active_drivers, i: Users2 }, { l: 'Vehicles', v: data.totals.vehicles, i: Truck }, { l: 'Documents', v: data.totals.documents, i: FileText }].map((m) => { const Ic = m.i; return (
                <Card key={m.l} className="flex items-center gap-3 p-4"><Ic className="h-6 w-6 text-[hsl(var(--primary))]" /><div><div className="text-xs uppercase tracking-wide text-muted-foreground">{m.l}</div><div className="font-display text-2xl font-bold">{m.v}</div></div></Card>
              ); })}
            </div>
            <div className="mb-5 grid gap-4 sm:grid-cols-3">
              {[['Compliant', data.compliance.compliant, 'compliant'], ['Warning', data.compliance.warning, 'warning'], ['Non-Compliant', data.compliance.non_compliant, 'non_compliant']].map(([l, v, s]) => (
                <Card key={l} className="flex items-center justify-between p-4"><StatusBadge status={s} label={l} /><span className="font-display text-2xl font-bold">{v}</span></Card>
              ))}
            </div>
            <Card className="overflow-hidden">
              <div className="border-b p-4 font-display font-semibold">Driver Qualification Summary</div>
              <div className="overflow-x-auto"><Table>
                <TableHeader><TableRow><TableHead>Driver</TableHead><TableHead>Compliance</TableHead><TableHead>Missing</TableHead><TableHead>Expired</TableHead></TableRow></TableHeader>
                <TableBody>{data.drivers.map((d, i) => (
                  <TableRow key={i}><TableCell className="font-medium">{d.name}</TableCell><TableCell><StatusBadge status={d.status} /></TableCell><TableCell className="text-sm text-muted-foreground">{d.missing.join(', ') || '—'}</TableCell><TableCell className="text-sm text-[hsl(var(--danger))]">{d.expired.join(', ') || '—'}</TableCell></TableRow>
                ))}</TableBody>
              </Table></div>
            </Card>
            <p className="mt-6 text-center text-xs text-muted-foreground">Built by ServoApex Engineering Ltd. © 2026</p>
          </>
        )}
      </main>
    </div>
  );
}
